import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';

import { type CardDisputeEvidence } from '@column/api';
import { Button, Icon } from '@column/column-ui-kit';
import {
  DataTable,
  type DataTableColumnDef,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Timestamp,
} from '@column/ui';

import { ROUTE } from '~/app/routes';
import { Section } from '~/components';
import { useCanManageCardDisputes, useDownloadCardDisputeDocument } from '~/lib/api/cardDisputes';
import { CARD_DISPUTE_EVIDENCE_TYPE_LABELS, DISPUTE_SYNC_NOTE } from '~/lib/disputes/labels';
import { useNotificationStore } from '~/stores/Notification';
import { Inner } from '~/styles';
import { formatDateTime, formatString } from '~/util';

import { CardDisputeProps } from './CardDispute';
import { getEvidenceErrorMessage } from './Evidence/evidence';
import { AddDisputeEvidence } from './Evidence/index';

interface EvidenceTableProps {
  disputeId: string;
  entries: CardDisputeEvidence[];
  emptyTitle: string;
  emptyDescription: string;
}

const getEvidenceTypeLabel = (evidenceType?: string): string => {
  if (!evidenceType) {
    return 'Narrative';
  }
  return (CARD_DISPUTE_EVIDENCE_TYPE_LABELS as Record<string, string>)[evidenceType] ?? formatString(evidenceType);
};

const getEvidenceState = (evidence: CardDisputeEvidence): string => {
  if (evidence.direction === 'inbound') {
    return 'Received from network';
  }
  return evidence.submitted_at ? 'Submitted to the Card Network' : 'Attached to dispute';
};

const EvidenceTable = ({ disputeId, entries, emptyTitle, emptyDescription }: EvidenceTableProps) => {
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);
  const downloadMutation = useDownloadCardDisputeDocument();

  const handleDownload = (evidence: CardDisputeEvidence) => {
    if (!evidence.document_id) {
      return;
    }

    downloadMutation.mutate(
      { documentId: evidence.document_id, evidenceId: evidence.id },
      {
        onError: (error) => {
          addDangerNotification({
            content: getEvidenceErrorMessage(error, 'Failed to download evidence document. Try Download again.'),
          });
        },
      }
    );
  };

  const columns: DataTableColumnDef<CardDisputeEvidence>[] = [
    {
      accessor: 'evidence_type',
      header: 'Category',
      width: 'minmax(min-content, 180px)',
      cell: ({ row }) => <span>{getEvidenceTypeLabel(row.evidence_type)}</span>,
    },
    {
      accessor: 'document_id',
      header: 'Evidence',
      width: 'minmax(240px, 2fr)',
      className: 'min-w-0',
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col gap-1">
          <span className="line-clamp-2 min-w-0 whitespace-pre-wrap text-muted-foreground">
            {row.narrative ?? (row.document_id ? 'Document evidence' : '—')}
          </span>
          {row.document_id && (
            <span className="truncate font-mono text-xs text-muted-foreground">{row.document_id}</span>
          )}
        </div>
      ),
    },
    {
      accessor: 'direction',
      header: 'State',
      width: 'minmax(min-content, 160px)',
      cell: ({ row }) => <span>{getEvidenceState(row)}</span>,
    },
    {
      accessor: 'submitted_at',
      header: 'Network Delivery',
      width: 'min-content',
      cell: ({ row }) =>
        row.submitted_at ? (
          <Timestamp
            date={row.submitted_at}
            formatHover={(hoverDate) => formatDateTime(hoverDate, { timeZoneFormat: 'utc' })}
          >
            {formatDateTime(row.submitted_at, { timeZoneFormat: 'local' })}
          </Timestamp>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      accessor: 'created_at',
      header: 'Added',
      width: 'min-content',
      cell: ({ row }) => (
        <Timestamp date={row.created_at}>{formatDateTime(row.created_at, { timeZoneFormat: 'local' })}</Timestamp>
      ),
    },
    {
      accessor: 'card_dispute_event_id',
      header: 'Related Response',
      width: 'min-content',
      cell: ({ row }) =>
        row.card_dispute_event_id ? (
          <Link
            className="whitespace-nowrap text-primary hover:underline"
            to={`${ROUTE.CARD_DISPUTES}/edit/${disputeId}#dispute-history`}
            title={row.card_dispute_event_id}
          >
            View history
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      accessor: 'id',
      header: '',
      width: 'min-content',
      cell: ({ row }) =>
        row.document_id ? (
          <Button
            variant="secondary"
            size="small"
            ariaLabel={`Download evidence ${row.id}`}
            onClick={() => handleDownload(row)}
            icon={
              <Icon.AnimationDownload
                running={downloadMutation.isPending && downloadMutation.variables?.evidenceId === row.id}
              />
            }
            isDisabled={downloadMutation.isPending && downloadMutation.variables?.evidenceId === row.id}
          >
            Download
          </Button>
        ) : null,
    },
  ];

  if (entries.length === 0) {
    return (
      <Empty className="min-h-[160px]">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icon.Document />
          </EmptyMedia>
          <EmptyTitle>{emptyTitle}</EmptyTitle>
          <EmptyDescription>{emptyDescription}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <DataTable<CardDisputeEvidence> columns={columns} data={entries} rowKey={(row) => row.id} rowClassName="min-h-11" />
  );
};

export const PageCardDisputeEvidence: React.FC = () => {
  const { cardDispute } = useOutletContext<CardDisputeProps>();
  const canManage = useCanManageCardDisputes();

  if (!cardDispute) {
    return null;
  }

  const outboundEvidence = cardDispute.evidence.filter((entry) => entry.direction === 'outbound');
  const inboundEvidence = cardDispute.evidence.filter((entry) => entry.direction === 'inbound');
  const terminal = cardDispute.stage === 'dispute_won' || cardDispute.stage === 'dispute_close';

  return (
    <Inner p={0} data-dd-privacy="hidden" data-testid="card-dispute-evidence-workspace">
      <Section headline="Supplied by you">
        <EvidenceTable
          disputeId={cardDispute.id}
          entries={outboundEvidence}
          emptyTitle="No evidence attached"
          emptyDescription="Evidence attached to this dispute will appear here."
        />
        {canManage && !terminal && <AddDisputeEvidence dispute={cardDispute} />}
      </Section>

      <Section
        headline="Received from network"
        description={`Network evidence appears here automatically. ${DISPUTE_SYNC_NOTE}`}
      >
        <EvidenceTable
          disputeId={cardDispute.id}
          entries={inboundEvidence}
          emptyTitle="Nothing received from the network yet"
          emptyDescription={`Network evidence appears here automatically. ${DISPUTE_SYNC_NOTE}`}
        />
      </Section>
    </Inner>
  );
};
